import { ruleImpl } from "./rule-impl";
import { arePriceVersionsOfOneProduct } from "./price-versions";
import { findMentionedAssets, type MentionableAsset } from "./mentioned-assets";
import { comparisonParticipantCount, tooFewComparisonParticipants } from "./asset-comparison-coherence";
import { OPTION_LIST_QUESTION_TYPES } from "./question-capabilities";
import { rankingCountsHeld, selectionCountsHeld } from "./validator";

/**
 * What removing tested assets would break, question by question (S1.3
 * `remove_asset`): the refusals the operations applier raises at Save, and
 * the editor's live check shows on the cards while the removal is unsaved
 * (E-session-10). One detector, read from typed refs, bindings, rows and
 * `@` mentions only. The applier's cleanup of what only pointed at a removed
 * asset (a ref, an answer, a row) is not here: that is allowed, and said as
 * an effect at Save.
 */
export type AssetRemovalRefusalCode =
  | "comparison_below_two"
  | "asset_subject_removed"
  | "choice_list_below_minimum"
  | "grid_rows_emptied"
  | "tested_asset_rows_emptied"
  | "asset_mentioned";

export interface AssetRemovalRefusal {
  /** The removed asset the refusal is about. */
  assetId: string;
  code: AssetRemovalRefusalCode;
  questionStableKey: string;
  /** Where on the question, as the applier's dependent path. */
  path: string;
}

type Json = Record<string, unknown>;
type RemovableAsset = MentionableAsset & { price?: unknown };

const isRecord = (value: unknown): value is Json =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const records = (value: unknown): Array<Json> =>
  Array.isArray(value) ? value.filter(isRecord) : [];

/** The id of the first removed asset an answer, row or category stands for, else null. */
export function removedAssetOf(
  item: unknown,
  removedIds: ReadonlySet<string>,
): string | null {
  const refs = isRecord(item) ? records(item.assetRefs) : [];
  const ref = refs.find((candidate) => removedIds.has(String(candidate.assetId)));
  return ref ? String(ref.assetId) : null;
}

/**
 * The list of a response whose items stand for assets and leave with them:
 * a categorical grid's categories, or a select's or ranking's options.
 */
export function removalListSurface(response: Json): "options" | "categories" | null {
  if (response.kind === "matrix_categorical") return "categories";
  return (OPTION_LIST_QUESTION_TYPES as ReadonlySet<unknown>).has(response.kind)
    ? "options"
    : null;
}

/** The response lists whose items carry text, as the applier names them. */
const TEXT_ITEM_LISTS: ReadonlyArray<{
  name: string;
  list: (response: Json) => unknown;
}> = [
  { name: "options", list: (response) => response.options },
  { name: "categories", list: (response) => response.categories },
  {
    name: "rows.rows",
    list: (response) =>
      isRecord(response.rows) && response.rows.source === "fixed"
        ? response.rows.rows
        : undefined,
  },
  { name: "measures", list: (response) => response.measures },
];

/**
 * The refusals of removing `removed` from a catalog that keeps `kept`, over
 * `questions` as they stand before the removal. Per question, in the order
 * the applier checks them: the comparison, the subject, the answer list, the
 * rows, the text.
 */
export function assetRemovalRefusals(args: {
  questions: ReadonlyArray<unknown>;
  kept: ReadonlyArray<RemovableAsset>;
  removed: ReadonlyArray<RemovableAsset>;
}): Array<AssetRemovalRefusal> {
  const { kept, removed } = args;
  if (removed.length === 0) return [];
  const removedIds = new Set(removed.map((asset) => asset.id));
  const preRemovalCatalog = [...kept, ...removed];
  const dependsOnRemoved = (item: unknown) => removedAssetOf(item, removedIds);
  const refusals: Array<AssetRemovalRefusal> = [];
  for (const question of records(args.questions)) {
    const key = String(question.stableKey ?? question.id);
    const refuse = (assetId: string, code: AssetRemovalRefusalCode, path: string) =>
      refusals.push({ assetId, code, questionStableKey: key, path });

    // A comparison left with fewer participants than an asset choice offers
    // needs an authored replacement, not a silent downgrade.
    const refs = records(question.assetRefs);
    const removedComparison = refs.find(
      (ref) => ref.role === "comparison" && removedIds.has(String(ref.assetId)),
    );
    if (removedComparison) {
      // Counted as the gate counts (`comparisonParticipantCount`: distinct
      // catalog-backed participants), before and after the removal.
      const before = comparisonParticipantCount(question, new Set(preRemovalCatalog.map((asset) => asset.id)));
      const after = comparisonParticipantCount(question, new Set(kept.map((asset) => asset.id)));
      if (!tooFewComparisonParticipants(before) && tooFewComparisonParticipants(after)) {
        refuse(String(removedComparison.assetId), "comparison_below_two", "assetRefs");
      }
    }

    const attribution = isRecord(question.planAttribution) ? question.planAttribution : {};
    const subjectKey = attribution.subjectAssetStableKey;
    const subject = subjectKey
      ? removed.find((asset) => asset.stableKey === subjectKey)
      : undefined;
    if (subject) {
      refuse(subject.id, "asset_subject_removed", "planAttribution.subjectAssetStableKey");
    }

    const response = isRecord(question.response) ? question.response : null;
    if (!response) continue;
    // The answers an asset stands for leave with it; a list left with fewer
    // than two real answers is no question.
    const surface = removalListSurface(response);
    const pruned = new Set<unknown>();
    if (surface && Array.isArray(response[surface])) {
      const items = records(response[surface]);
      const leaving = items.filter((item) => dependsOnRemoved(item));
      leaving.forEach((item) => pruned.add(item));
      if (leaving.length > 0) {
        const realAnswers = items.filter(
          (item) => !pruned.has(item) && item.exclusive !== true,
        ).length;
        if (realAnswers < 2) {
          refuse(dependsOnRemoved(leaving[0])!, "choice_list_below_minimum", `response.${surface}`);
        }
      }
    }
    const rows = isRecord(response.rows) ? response.rows : null;
    if (rows?.source === "fixed" && Array.isArray(rows.rows)) {
      const all = records(rows.rows);
      const leaving = all.filter((row) => dependsOnRemoved(row));
      leaving.forEach((row) => pruned.add(row));
      if (leaving.length > 0 && leaving.length === all.length) {
        refuse(dependsOnRemoved(all[0])!, "grid_rows_emptied", "response.rows.rows");
      }
    } else if (rows?.source === "tested_assets") {
      const typeRemoved = removed.find((asset) => asset.type === rows.assetType);
      if (typeRemoved && !kept.some((asset) => asset.type === rows.assetType)) {
        refuse(typeRemoved.id, "tested_asset_rows_emptied", "response.rows");
      }
    }

    // Text that still @mentions a removed asset would ship a broken
    // reference. An answer or row that leaves with its asset takes its text
    // with it; a mention of a product keeps naming the price versions that
    // stay.
    const texts: Array<{ path: string; text: unknown }> = [
      { path: "title", text: question.title },
      { path: "description", text: question.description },
    ];
    for (const { name, list } of TEXT_ITEM_LISTS) {
      const items = list(response);
      // Numbered in the list the Save leaves (without the items that leave).
      const left = (Array.isArray(items) ? items : []).filter((item) => !pruned.has(item));
      left.forEach((item, itemIndex) => {
        const fields = isRecord(item) ? item : {};
        texts.push({ path: `response.${name}[${itemIndex}].label`, text: fields.label });
        texts.push({ path: `response.${name}[${itemIndex}].description`, text: fields.description });
      });
    }
    for (const { path, text } of texts) {
      const mentioned = findMentionedAssets(text, preRemovalCatalog).find(
        (asset) =>
          removedIds.has(asset.id) &&
          !kept.some((stays) => arePriceVersionsOfOneProduct([asset, stays])),
      );
      if (mentioned) refuse(mentioned.id, "asset_mentioned", path);
    }
  }
  return refusals;
}

/**
 * The refusal code of a condition whose every answer or row a removal takes:
 * the applier's `dependent_logic`, decided once by `leafAfterAnswersLeave` and
 * raised by the Save.
 */
export const DEPENDENT_LOGIC_CODE = "dependent_logic" as const;

/** One thing that leaves a question because the asset it stands for was removed. */
export interface AssetRemovalLeave {
  questionStableKey: string;
  /** The removed asset that took it. */
  assetId: string;
  /**
   * `ref`: a ref of the question, or the lane skip of the asset's version.
   * `choice`: an answer or category that stood for the asset. `row`: a fixed
   * row that stood for it. `tested_row`: the asset's row in a tested-assets
   * grid.
   */
  kind: "ref" | "choice" | "row" | "tested_row";
  itemId?: string;
  value?: unknown;
  /** Where a `choice` stood: a select's options or a categorical grid's categories. */
  surface?: "options" | "categories";
}

/**
 * The cleanup of removing `removed`, the applier's own (S1.3): what only
 * pointed at a removed asset leaves `questions`, which are mutated (the
 * applier's, or the editor's copy of its draft): a question's refs, a lane
 * skip of a removed version, the answers, categories and fixed rows that stand
 * for it; a ranking or multi-select it shortens keeps its stated answer
 * counts within the answers left. Returns what left, in the order the applier says it; the Save
 * records each as an effect, and the editor reads them from the server's dry
 * run of its Save, so there is one walk.
 */
export function projectAssetRemoval(
  questions: ReadonlyArray<Json>,
  removed: ReadonlyArray<RemovableAsset>,
): Array<AssetRemovalLeave> {
  const leaves: Array<AssetRemovalLeave> = [];
  if (removed.length === 0) return leaves;
  const removedIds = new Set(removed.map((asset) => asset.id));
  for (const question of questions) {
    const key = String(question.stableKey ?? question.id);
    const refs = records(question.assetRefs);
    const gone = refs.filter((ref) => removedIds.has(String(ref.assetId)));
    gone.forEach((ref) =>
      leaves.push({ questionStableKey: key, assetId: String(ref.assetId), kind: "ref" }),
    );
    if (gone.length > 0) {
      question.assetRefs = refs.filter((ref) => !removedIds.has(String(ref.assetId)));
    }
    // A removed asset's lane is gone, and with it the copy its lane exclusion
    // skipped (D-b).
    if (Array.isArray(question.laneExclusions)) {
      const kept = records(question.laneExclusions).filter((exclusion) => {
        const lane = removed.find((asset) => asset.stableKey === exclusion.assetStableKey);
        if (lane) leaves.push({ questionStableKey: key, assetId: lane.id, kind: "ref" });
        return !lane;
      });
      if (kept.length > 0) question.laneExclusions = kept;
      else delete question.laneExclusions;
    }
    const response = isRecord(question.response) ? question.response : null;
    if (!response) continue;
    const surface = removalListSurface(response);
    if (surface && Array.isArray(response[surface])) {
      const items = response[surface] as Array<Json>;
      const kept: Array<Json> = [];
      for (const item of items) {
        const assetId = removedAssetOf(item, removedIds);
        if (!assetId) {
          kept.push(item);
          continue;
        }
        leaves.push({
          questionStableKey: key,
          assetId,
          kind: "choice",
          itemId: String(item.id),
          value: item.value,
          surface,
        });
      }
      if (kept.length !== items.length) {
        response[surface] = kept;
        // The list's stated answer counts stay inside what it keeps, as the
        // editor's own option removal holds them.
        if (response.kind === "ranking") Object.assign(response, rankingCountsHeld(response));
        if (response.kind === "multi_select") Object.assign(response, selectionCountsHeld(response));
      }
    }
    const grid = isRecord(response.rows) ? response.rows : null;
    if (grid?.source === "fixed" && Array.isArray(grid.rows)) {
      const rows = grid.rows as Array<Json>;
      const kept = rows.filter((row) => !removedAssetOf(row, removedIds));
      if (kept.length !== rows.length) {
        rows.forEach((row) => {
          const assetId = removedAssetOf(row, removedIds);
          if (assetId) {
            leaves.push({
              questionStableKey: key,
              assetId,
              kind: "row",
              itemId: String(row.id),
              value: row.value,
            });
          }
        });
        grid.rows = kept;
      }
    } else if (grid?.source === "tested_assets") {
      removed
        .filter((asset) => asset.type === grid.assetType)
        .forEach((asset) =>
          leaves.push({ questionStableKey: key, assetId: asset.id, kind: "tested_row", value: asset.id }),
        );
    }
  }
  return leaves;
}
// Each rule below is decided by the function named (wave H T3, move 11).
ruleImpl('asset_mentioned', assetRemovalRefusals);
ruleImpl('asset_subject_removed', assetRemovalRefusals);
ruleImpl('choice_list_below_minimum', assetRemovalRefusals);
ruleImpl('comparison_below_two', assetRemovalRefusals);
ruleImpl('grid_rows_emptied', assetRemovalRefusals);
ruleImpl('tested_asset_rows_emptied', assetRemovalRefusals);
